import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import { useLazyQuery } from "@apollo/client";
import { type IntlShape, useIntl } from "react-intl";

import { type LensSubmissionFlowState } from "../../submit/models";
import { type UserProfileQuery, type UserProfileQueryVariables, getUserProfile } from "../api/queries";
import { type ApolloResult } from "../models";

import { messages } from "./messages.generated";

const getDefaultOrganizationAndLensSpaceNames = (
    intl: IntlShape,
    data?: UserProfileQuery,
): {
    defaultLensSpaceName: string;
    defaultOrganizationName: string;
} => {
    let defaultOrganizationName = "";
    let defaultLensSpaceName = "";
    if (data) {
        const name = data.getUserProfile.userDisplayName;
        defaultOrganizationName = intl.formatMessage(messages.organization);
        defaultLensSpaceName = intl.formatMessage(messages.lensFolder);

        if (name) {
            defaultOrganizationName = `${name} ${defaultOrganizationName}`;
            defaultLensSpaceName = `${name} ${defaultLensSpaceName}`;
        }
    }
    return {
        defaultOrganizationName,
        defaultLensSpaceName,
    };
};

export const useDefaultOrganizationAndLensSpaceNames = (
    forceUseDefaultNames?: boolean,
): ApolloResult<{
    defaultLensSpaceName: string;
    defaultOrganizationName: string;
}> => {
    const intl = useIntl();

    const [fetchUserProfile, { data, error, loading }] = useLazyQuery<UserProfileQuery, UserProfileQueryVariables>(
        getUserProfile,
    );
    const location = useLocation();
    const locationState = location.state as LensSubmissionFlowState | undefined;

    useEffect(() => {
        const shouldUseDefaultNames = !!forceUseDefaultNames || locationState?.useDefaultNames;
        if (shouldUseDefaultNames) {
            void fetchUserProfile();
        }
    }, [locationState, fetchUserProfile, forceUseDefaultNames]);

    const { defaultOrganizationName, defaultLensSpaceName } = getDefaultOrganizationAndLensSpaceNames(intl, data);

    return {
        error,
        loading,
        defaultLensSpaceName,
        defaultOrganizationName,
    };
};
